<template>
  <div ref="avatarroot">
    <div
      v-if="value"
      class="group relative bg-cover w-10 h-10 bg-primary-core border border-white-50% flex items-center justify-center rounded-lg"
      :style="{
        'background-color': stringToColor(value.name, 60, 40)
      }"
    >
      <img
        v-if="profileImageUri"
        :src="profileImageUri"
        class="w-11 h-11 object-cover border border-white-10% rounded-lg"
      />
      <span v-else class="text-white text-22 font-semibold">
        {{ value.initials }}
      </span>

      <InfoTooltip v-if="value.showTooltip" :value="value.name"></InfoTooltip>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, defineAsyncComponent } from "vue";
import { useSectionsStore } from "@/stores/sections";

const InfoTooltip = defineAsyncComponent(() => import("@/components/Shared/InfoTooltip.vue"));

defineOptions({ name: "SectionAvatar" });

interface Props {
  section?: any;
  tooltip?: string | boolean | null;
  autoloadProfilePhoto?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  section: () => ({}),
  tooltip: null,
  autoloadProfilePhoto: false
});

const sectionsStore = useSectionsStore();

const value = ref<any>(null);
const profileImageUri = ref<any>(null);
const avatarroot = ref<HTMLElement | null>(null);

const stringToColor = (str: string, saturation: number, lightness: number) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = hash % 360;
  return "hsl(" + h + ", " + saturation + "%, " + lightness + "%)";
};

const setupLoadPhotoObserver = () => {
  if (!value.value.extSysApiSectionId) return;
  const options = {
    root: null,
    threshold: 0
  };

  const observer = new IntersectionObserver(([elm]) => {
    if (elm.isIntersecting) {
      sectionsStore.sectionsLoadProfilePicture(value.value.extSysApiSectionId).then(result => {
        profileImageUri.value = result;
      });
      if (avatarroot.value) observer.unobserve(avatarroot.value);
    }
  }, options as any);

  if (avatarroot.value) observer.observe(avatarroot.value);
};

onMounted(() => {
  const val: any = {};
  if (props.section.name) {
    val.name = props.section.name;
    val.initials = props.section.name.substring(0, 2);
  }

  if (props.section.extSysApiSectionId) {
    val.extSysApiSectionId = props.section.extSysApiSectionId;
  }

  val.tooltip = !!props.tooltip;

  value.value = val;

  if (props.autoloadProfilePhoto) {
    setupLoadPhotoObserver();
  }
});
</script>

<style scoped>
img {
  max-width: 2.75rem;
  max-height: 2.75rem;
}
</style>
